import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";
import Tooltip from "@mui/material/Tooltip";
import { format } from "date-fns";

import { useModelPopup } from "../../../../../../common/hooks";
import styles from "../projectDetails.module.scss";
import { isANumber, makeProjectField } from "../../../../../../common/helper";
import ProjectFieldsEdit from "modules/projects/project/overview/projectDetails/projectFieldsEdit.component";
import { Icon, smSize } from "../../../../../../common/icon";
import { currencySymbolObj } from "../../../../../../common/constants";

const ProjectDetailsWithoutGroup = ({
  canManageTasks,
  metrics,
  setEditMetrics,
  isAdmin,
  group,
  expandAll,
}) => {
  const popup = useModelPopup();
  const selectedCouncil = useSelector((state) => state.councilReducer.selectedCouncil);

  const [expanded, setExpanded] = useState(!group.collapse);

  useEffect(() => {
    if (!expandAll) {
      setExpanded(!group.collapse);
      return;
    }
    setExpanded(expandAll);
  }, [expandAll]);

  const handleGroupEdit = (group) => {
    if (group?.name === "Implementation" && selectedCouncil?.name === "Ford") {
      setEditMetrics();
      return;
    }
    const newGroup = {
      ...group,
      project_fields: group.project_fields.map(makeProjectField),
    };

    popup.show({
      title: `Edit ${newGroup.name || ""} group`,
      component: <ProjectFieldsEdit selectedGroup={newGroup} />,
    });
  };

  const canManageReadyForReplication = useCallback(
    (group) => {
      if (group.name !== "Ready for Replication") return canManageTasks;

      return isAdmin;
    },
    [selectedCouncil, isAdmin, canManageTasks]
  );

  const makeHeaderClasses = useMemo(() => {
    return `${styles[group.status]} d-flex align-items-center w-100 px-2`;
  }, [group.status]);

  const handleExpand = () => setExpanded((prev) => !prev);

  return (
    <div className="mb-3">
      <div className={makeHeaderClasses}>
        <h5 onClick={handleExpand} className={`${styles.groupHeaderName} cursor-pointer font-weight-bold d-flex align-items-center justify-content-center w-100 mb-0`}>
          {group.name}
        </h5>
        <div
          className="d-flex justify-content-center cursor-pointer "
          onClick={handleExpand}
        >
          <Icon
            {...smSize}
            icon="icn-table-down-chevron"
            className={classNames(
              styles.icon,
              expanded ? styles.collapse : styles.collapseUp,
            )}
          />
        </div>
      </div>
      {
        expanded && (
          <div className="px-2" style={{ boxShadow: '0 1px 3px 0 rgba(27, 36, 50, 0.15)' }}>
            <div className="d-flex align-items-end justify-content-end position-relative">
              {canManageReadyForReplication(group) && (
                <div
                  className="cursor-pointer"
                  onClick={() => handleGroupEdit(group)}
                >
                  <Icon {...smSize} icon="icn-edit" />
                </div>
              )}
            </div>
            {group?.name === "Implementation" &&
            selectedCouncil?.name === "Ford" ? (
              <div className="d-flex flex-column align-items-center justify-content-center mt-2">
                <span className="small font-weight-bold">
                  Job 1 Date
                </span>
                <div className="caption mt-2">
                  {metrics.job_1_date
                    ? format(
                      new Date(metrics.job_1_date),
                      "MMM do, yyyy",
                    )
                    : "-"}
                </div>
              </div>
            ) : (
              <div className="d-flex flex-row flex-wrap mt-2">
                {[
                  ...group?.project_fields,
                  ...(group.project_metrics?.map(m => ({
                    ...m,
                    field_name: m.name,
                    field_value: m.value,
                    isMetricField: true
                  })) || [])
                ]
                  .filter(
                    (f) =>
                      f.field_name !==
                      "Possible Replications (Plants)" &&
                      f.field_name !==
                      "Possible Replications (Areas)",
                  )
                  .map((field) => {
                    return (
                      <div
                        key={field.project_field_template_id}
                        className={classNames(
                          styles.infoCol,
                          "pl-0 mb-3 pr-3",
                          field?.big_box_text ? "w-50" : "",
                          field.field_name === 'Annual Incremental Investment ($)' ? 'w-25' : ''
                        )}
                      >
                        <span className="small font-weight-bold">
                          {field.field_name}
                        </span>
                        {
                          field.isMetricField && (
                            <div className="caption mt-2">
                              {
                                field.type === '%' && field.field_value ? `${field.field_value.toLocaleString()}%` : ''
                              }
                              {
                                field.type === '$' && (
                                  isANumber(field?.field_value)
                                    ? `${field.type}${field.field_value.toLocaleString()}`
                                    : field.field_value
                                )
                              }
                            </div>
                          )
                        }
                        {field.field_type === "text" && (
                          <div>
                            {field.field_value?.length >
                            (!field?.big_box_text ? 40 : 330) ? (
                              <Tooltip
                                title={field.field_value}
                                placement="top"
                                arrow
                              >
                                <span className="cursor-pointer">
                                  {field.field_value.slice(
                                    0,
                                    !field?.big_box_text
                                      ? 40
                                      : 330,
                                  )}
                                  ...
                                </span>
                              </Tooltip>
                            ) : (
                              <div className="caption mt-2">
                                {field.field_value || "-"}
                              </div>
                            )}
                          </div>
                        )}
                        {field.field_type === "date" && (
                          <div>
                            {field.field_date ? (
                              <div className="caption mt-2">
                                {format(
                                  new Date(field.field_date),
                                  "MMM do, yyyy",
                                )}
                              </div>
                            ) : (
                              "-"
                            )}
                          </div>
                        )}
                        {(field.field_type === "number" ||
                          field.field_type === "currency") && (
                          <div>
                            {field.field_number ? (
                              <div className="caption mt-2">
                                {field.option_selected.length
                                  ? currencySymbolObj[field.option_selected[0]]
                                  : null}{" "}
                                {
                                  field.field_type === "currency" && isANumber(field?.field_number)
                                    ? field.field_number.toLocaleString()
                                    : field.field_number
                                }
                              </div>
                            ) : (
                              "-"
                            )}
                          </div>
                        )}
                        {field.field_type === "dropdown" && (
                          <div className="d-flex flex-column">
                            {field.multiselect ? (
                              field.option_selected?.length ? (
                                field.option_selected.map(
                                  (o, idx) => (
                                    <span
                                      className="caption"
                                      key={`${o}-${idx}`}
                                    >
                                {o}
                                      {field.option_selected
                                        ?.length === 1
                                        ? ""
                                        : field.option_selected
                                          ?.length ===
                                        idx + 1
                                          ? ""
                                          : ","}
                              </span>
                                  ),
                                )
                              ) : (
                                "-"
                              )
                            ) : (
                              <span className="caption">
                          {field.option_selected.length
                            ? field.option_selected[0]
                            : field.field_value || "-"}
                        </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        )
      }
    </div>
  );
};

export default React.memo(ProjectDetailsWithoutGroup);
